/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { useParams } from "react-router-dom";

import { useAuth, useNavigation } from "@/providers";
import { Page } from "@/shared/components";
import { OrgInviteRecord } from "@/shared/domain/orgs";

import { InviteResponseForm } from "./InviteResponseForm";

/**
 * The top level route for a user to accept or decline an invite to an organization.
 */
export function Invite() {
  const { setCurrentOrganization, availableOrganizations } = useAuth();
  const { goto } = useNavigation();

  const { inviteId } = useParams();

  const onAccepted = (invite: OrgInviteRecord) => {
    setCurrentOrganization(invite.org);
    goto.home();
  };

  const onDeclined = () => goto.home();

  /**
   * If a referenced invite is missing, but they're already in orgs, we'll send them to the org picker.
   *
   * The most common scenario where this happens is when an invite is sent for an org with an "automatically add
   * users from this domain" setting, and the user will join the org through that rule before they get a chance
   * to accept the invite. This automatic step will delete the invite.
   *
   * For other cases where an invite doesn't exist for a user who has orgs to fall back on, redirecting to the org
   * picker is a reasonable fallback. For cases where a user doesn't have any orgs, we can keep them on the page
   * which says they don't have any invites, and direct them towards support.
   */
  const onMissing = () => {
    if (availableOrganizations && availableOrganizations.length > 0) {
      goto.signIn({ chooseOrg: true });
    }
  };

  return (
    <Page title={"Roboto - Invite"}>
      <InviteResponseForm
        inviteId={inviteId}
        onAccepted={onAccepted}
        onDeclined={onDeclined}
        onMissing={onMissing}
      />
    </Page>
  );
}
